import React from "react";
import { useSearchParams } from "react-router-dom";
import type { BankAccount } from "@/hooks/use-bank-accounts";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { fmtDate } from "./matching-tabs/matching-ui";

// useReconciliationMatching removed - functionality moved to individual matching hooks
import { useMatchedPaidExpenses } from "@/hooks/finance/reconciliation/matching-hooks/useMatchedPaidExpenses";
import { useMatchedBankTransactions } from "@/hooks/finance/reconciliation/matching-hooks/useMatchedBankTransactions";
import { useUnmatchedPaidExpenses } from "@/hooks/finance/reconciliation/matching-hooks/useUnmatchedPaidExpenses";
import { useUnmatchedBankTransactions } from "@/hooks/finance/reconciliation/matching-hooks/useUnmatchedBankTransactions";
import { useExpenseCategories } from '@/hooks/finance/useExpenseCategories';
import type { useBankAccounts } from "@/hooks/use-bank-accounts";

import { TabMatchedBank } from "./matching-tabs/TabMatchedBank";
import { TabMatchedPaid } from "./matching-tabs/TabMatchedPaid";
import { TabUnmatchedBank } from "./matching-tabs/TabUnmatchedBank";
import { TabUnmatchedPaid } from "./matching-tabs/TabUnmatchedPaid";

type Props = {
  dateFrom: string;
  dateTo: string;
  bankAccountId?: string | null;
  accounts: BankAccount[];
};

const clamp = (p: number, totalPages: number) => {
  if (totalPages <= 1) return 1;
  if (p < 1) return 1;
  if (p > totalPages) return totalPages;
  return p;
};

export function ReconciliationMatchingTabs({ dateFrom, dateTo, bankAccountId, accounts }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const validTabs = ["unmatched-bank", "unmatched-paid", "matched-paid", "matched-bank"] as const;
  type TabKey = typeof validTabs[number];
  const urlTab = searchParams.get("tab") as TabKey | null;
  const tab: TabKey = urlTab && validTabs.includes(urlTab) ? urlTab : "unmatched-bank";
  const setTab = (v: TabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", v);
    setSearchParams(next, { replace: true });
  };

  const accountName = React.useMemo(() => {
    if (!bankAccountId) return null;
    return accounts.find((a) => a.id === bankAccountId)?.name ?? bankAccountId;
  }, [accounts, bankAccountId]);

  // pagination state per tab
  const [pageUnmatchedBank, setPageUnmatchedBank] = React.useState(1);
  const [pageUnmatchedPaid, setPageUnmatchedPaid] = React.useState(1);
  const [pageMatchedPaid, setPageMatchedPaid] = React.useState(1);
  const [pageMatchedBank, setPageMatchedBank] = React.useState(1);
  const qExpenseCategories = useExpenseCategories();
  const expenseCategories = qExpenseCategories.data ?? [];

  // reset pagination when filters change (so you don't land on empty page 7 after changing range)
  React.useEffect(() => {
    setPageUnmatchedBank(1);
    setPageUnmatchedPaid(1);
    setPageMatchedPaid(1);
    setPageMatchedBank(1);
  }, [dateFrom, dateTo, bankAccountId]);

  const qMatchedPaid = useMatchedPaidExpenses({
    dateFrom,
    dateTo,
    bankAccountId,
  });

  const qMatchedBank = useMatchedBankTransactions({
    dateFrom,
    dateTo,
    bankAccountId,
  });

  const qUnmatchedPaid = useUnmatchedPaidExpenses({
    dateFrom,
    dateTo,
    bankAccountId,
  });

  const qUnmatchedBank = useUnmatchedBankTransactions({
    dateFrom,
    dateTo,
    bankAccountId,
  });
  const refetchUnmatchedBank = qUnmatchedBank.refetch;

  const matchedPaid = qMatchedPaid.data?.matchedPaid ?? [];
  const matchedBank = qMatchedBank.data?.matchedBank ?? [];
  const unmatchedPaid = qUnmatchedPaid.data?.unmatchedPaid ?? [];
  const unmatchedBank = qUnmatchedBank.data?.unmatchedBank ?? [];

  const counts = React.useMemo(
    () => ({
      ub: unmatchedBank.length,
      up: unmatchedPaid.length,
      mb: matchedBank.length,
      mp: matchedPaid.length,
    }),
    [unmatchedBank.length, unmatchedPaid.length, matchedBank.length, matchedPaid.length]
  );

  const Count = ({ n }: { n: number }) => (
    <Badge
      variant="secondary"
      className="ml-2 h-5 px-2 text-[10px] font-black tabular-nums"
      title={`${n}`}
    >
      {n}
    </Badge>
  );

  // PAGE_SIZE берётся из matching-ui (10), но тут мы не импортируем его.
  // Держим число синхронно: у тебя PAGE_SIZE = 10.
  const PAGE_SIZE = 10;

  const totalUP = Math.max(1, Math.ceil(unmatchedPaid.length / PAGE_SIZE));
  const totalUB = Math.max(1, Math.ceil(unmatchedBank.length / PAGE_SIZE));
  const totalMP = Math.max(1, Math.ceil(matchedPaid.length / PAGE_SIZE));
  const totalMB = Math.max(1, Math.ceil(matchedBank.length / PAGE_SIZE));

  // clamp pages if list size changed
  React.useEffect(() => {
    setPageUnmatchedPaid((p) => clamp(p, totalUP));
  }, [totalUP]);

  React.useEffect(() => {
    setPageUnmatchedBank((p) => clamp(p, totalUB));
  }, [totalUB]);

  React.useEffect(() => {
    setPageMatchedPaid((p) => clamp(p, totalMP));
  }, [totalMP]);

  React.useEffect(() => {
    setPageMatchedBank((p) => clamp(p, totalMB));
  }, [totalMB]);

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-slate-900">Matching</div>
            <div className="mt-1 text-[11px] text-slate-600">
              Range: <span className="font-semibold text-slate-800">{fmtDate(dateFrom)}</span> →{" "}
              <span className="font-semibold text-slate-800">{fmtDate(dateTo)}</span>
              {bankAccountId ? (
                <>
                  {" "}
                  · Account: <span className="font-semibold text-slate-800">{accountName}</span>
                </>
              ) : null}
            </div>
          </div>
        </div>

        <div className="mt-3">
          <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
            <TabsList>
              <TabsTrigger value="unmatched-bank" className="flex items-center">
                Unmatched bank transactions <Count n={counts.ub} />
              </TabsTrigger>

              <TabsTrigger value="unmatched-paid" className="flex items-center">
                Unmatched paid expenses <Count n={counts.up} />
              </TabsTrigger>

              <TabsTrigger value="matched-bank" className="flex items-center">
                Matched bank transactions <Count n={counts.mb} />
              </TabsTrigger>

              <TabsTrigger value="matched-paid" className="flex items-center">
                Matched paid expenses <Count n={counts.mp} />
              </TabsTrigger>
            </TabsList>

            {/* вкладки */}
            <TabUnmatchedBank
              isLoading={qUnmatchedBank.isLoading}
              error={qUnmatchedBank.error}
              unmatchedBank={unmatchedBank}
              pageUnmatchedBank={pageUnmatchedBank}
              setPageUnmatchedBank={setPageUnmatchedBank}
              totalUB={totalUB}
              categories={expenseCategories}
              refetchUnmatchedBank={refetchUnmatchedBank}
              accounts={accounts}
            />

            <TabUnmatchedPaid
              isLoading={qUnmatchedPaid.isLoading}
              error={qUnmatchedPaid.error}
              unmatchedPaid={unmatchedPaid}
              pageUnmatchedPaid={pageUnmatchedPaid}
              setPageUnmatchedPaid={setPageUnmatchedPaid}
              totalUP={totalUP}
            />

            <TabMatchedPaid
              isLoading={qMatchedPaid.isLoading}
              error={qMatchedPaid.error}
              matchedPaid={matchedPaid}
              pageMatchedPaid={pageMatchedPaid}
              setPageMatchedPaid={setPageMatchedPaid}
              totalMP={totalMP}
            />

            <TabMatchedBank
              isLoading={qMatchedBank.isLoading}
              error={qMatchedBank.error}
              matchedBank={matchedBank}
              pageMatchedBank={pageMatchedBank}
              setPageMatchedBank={setPageMatchedBank}
              totalMB={totalMB}
              accounts={accounts}
            />
          </Tabs>
        </div>
      </div>
    </div>
  );
}